import { DraftAttachmentDoc, DraftLabelDoc, ThreadVisibility } from "libs/schema";
import { IRecipientOption } from "~/components/forms/ThreadRecipients";
import { toast } from "~/environment/toast-service";
import { debouncedWrite, withActionContext, write } from "./write";
import dayjs from "dayjs";
import { EnqueueOptions } from "~/environment/TransactionQueue";
import * as actions from "libs/actions";
import { reindexNotification } from "./notification";
import { DraftRecipient, EditorMention } from "libs/actions/draft";
import { DateTime } from "luxon";

export function mapRecipientOptionToDraftRecipient(option: IRecipientOption): DraftRecipient {
  return { type: option.type, id: option.value };
}

/* -------------------------------------------------------------------------------------------------
 *  createNewThreadDraft
 * -------------------------------------------------------------------------------------------------
 */

export interface CreateNewThreadDraftProps {
  type: "COMMS" | "EMAIL";
  draftId: string;
  ownerOrganizationId: string;
  threadId: string;
  branchedFrom?: null | {
    threadId: string;
    messageId: string;
  };
  subject?: string | null;
  visibility?: ThreadVisibility | null;
  bodyHTML?: string;
  to?: DraftRecipient[];
  cc?: DraftRecipient[];
  bcc?: DraftRecipient[];
  userMentions?: EditorMention[];
  groupMentions?: EditorMention[];
  attachments?: DraftAttachmentDoc[];
}

export const createNewThreadDraft = withActionContext(
  async (context, environment, props: CreateNewThreadDraftProps) => {
    return {
      forward: async () => {
        const action = await actions.draft.createNewThreadDraft.action(props, context);

        await write(environment, { action });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 *  createReplyDraft
 * -------------------------------------------------------------------------------------------------
 */

export interface CreateReplyDraftProps {
  type: "COMMS" | "EMAIL";
  draftId: string;
  ownerOrganizationId: string;
  threadId: string;
  branchedFrom?: null | {
    threadId: string;
    messageId: string;
  };
  bodyHTML?: string;
  to?: DraftRecipient[];
  cc?: DraftRecipient[];
  bcc?: DraftRecipient[];
  userMentions?: EditorMention[];
  groupMentions?: EditorMention[];
  attachments?: DraftAttachmentDoc[];
}

export const createReplyDraft = withActionContext(async (context, environment, props: CreateReplyDraftProps) => {
  return {
    forward: async () => {
      const action = await actions.draft.createReplyDraft.action(props, context);

      await write(environment, { action });
    },
  };
});

/* -------------------------------------------------------------------------------------------------
 *  setDraft
 * -------------------------------------------------------------------------------------------------
 */

export interface SetDraftProps {
  type: "COMMS" | "EMAIL";
  draftId: string;
  ownerOrganizationId: string;
  is_reply: boolean;
  is_edit: boolean;
  threadId: string;
  branchedFrom: null | {
    threadId: string;
    messageId: string;
  };
  subject: string | null;
  bodyHTML: string;
  to: DraftRecipient[];
  cc: DraftRecipient[];
  bcc: DraftRecipient[];
  visibility: ThreadVisibility | null;
  userMentions: EditorMention[];
  groupMentions: EditorMention[];
  labels: DraftLabelDoc[];
  attachments: DraftAttachmentDoc[];
  noDebounce?: boolean;
}

/**
 * This is intended to be used for updates, but since it's implemented as a "set" I decided to name it that.
 * Prefer "createDraft" for creating a new draft. This function should not be used for "undeleting" a draft.
 * We attempt to guard against accidental undeletes by checking if the draft has been deleted before updating it.
 */
export const setDraft = withActionContext(async (context, environment, props: SetDraftProps) => {
  return {
    forward: async () => {
      const debounceOptions: EnqueueOptions["debounce"] | undefined =
        props.noDebounce ? undefined : { key: getDraftDebounceKey(props.draftId), debounceMs: 1500, maxWaitMs: 4000 };

      await debouncedWrite(environment, debounceOptions, async () => {
        const action = await actions.draft.setDraft.action(props, context);

        return { action };
      });
    },
  };
});

/* -------------------------------------------------------------------------------------------------
 *  sendDraft
 * -------------------------------------------------------------------------------------------------
 */

export interface SendDraftProps {
  type: "COMMS" | "EMAIL";
  draftId: string;
  ownerOrganizationId: string;
  is_reply: boolean;
  is_edit: boolean;
  threadId: string;
  branchedFrom?: null | {
    threadId: string;
    messageId: string;
  };
  subject?: string | null;
  bodyHTML: string;
  to: DraftRecipient[];
  cc: DraftRecipient[];
  bcc: DraftRecipient[];
  visibility?: ThreadVisibility | null;
  userMentions: EditorMention[];
  groupMentions: EditorMention[];
  labels: DraftLabelDoc[];
  attachments: DraftAttachmentDoc[];
  scheduledToBeSentAt: Date;
  afterUndo?: () => void;
  noToast?: boolean;
}

export const sendDraft = withActionContext(async (context, environment, props: SendDraftProps) => {
  const scheduledToBeSentAtTz = DateTime.now().zoneName;

  return {
    forward: async () => {
      // Wait for any debounced writes associated with the draft to finish being added to the tx queue.
      await debouncedWrite.flush([getDraftDebounceKey(props.draftId)]);

      const action = await actions.draft.sendDraft.action({ ...props, scheduledToBeSentAtTz }, context);

      await write(environment, {
        action,
        onOptimisticWrite: () => {
          reindexNotification(environment, { threadId: props.threadId });
        },
      });
    },
    backward: async () => {
      const canUndo = dayjs().isBefore(props.scheduledToBeSentAt);

      if (!canUndo) {
        throw new actions.InvalidActionStateError("[sendDraft] [backward] Too much time has passed ", {
          userMessage: "Cannot undo sending draft",
          userHint: "Too much time has passed",
        });
      }

      const action = await actions.message.unscheduleMessageToBeSent.action({ messageId: props.draftId }, context);

      await write(environment, {
        action,
        onOptimisticWrite: () => {
          toast("vanilla", {
            subject: "Unsending draft...",
          });

          reindexNotification(environment, { threadId: props.threadId });

          props.afterUndo?.();
        },
      });

      toast("vanilla", {
        subject: "Unsending draft...Done!",
      });
    },
  };
});

/* -------------------------------------------------------------------------------------------------
 *  deleteDraft
 * -------------------------------------------------------------------------------------------------
 */

export const deleteDraft = withActionContext(
  async (
    context,
    environment,
    props: {
      draftId: string;
      afterUndo?: () => void;
    },
  ) => {
    return {
      forward: async () => {
        environment.logger.info({ draftId: props.draftId }, `[deleteDraft] deleting draft ...`);

        // Wait for any debounced writes associated with the draft to finish being added to the tx queue.
        await debouncedWrite.flush([getDraftDebounceKey(props.draftId)]);

        const action = await actions.draft.deleteDraft.action({ id: props.draftId }, context);

        await write(environment, { action });
      },
      backward: async () => {
        environment.logger.info({ draftId: props.draftId }, `[deleteDraft] restoring draft ...`);

        // Wait for any debounced writes associated with the draft to finish being added to the tx queue.
        await debouncedWrite.flush([getDraftDebounceKey(props.draftId)]);

        const action = await actions.draft.unDeleteDraft.action({ id: props.draftId }, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: "Draft restored",
            });

            props.afterUndo?.();
          },
        });
      },
    };
  },
);

/* -----------------------------------------------------------------------------------------------*/

export function getDraftDebounceKey(draftId: string) {
  return `draft-${draftId}`;
}

/* -----------------------------------------------------------------------------------------------*/
